// The content-translation API surface for the operator SPA — CONTENT-TRANSLATION-ARCH §5.1.
// One BATCH resolve per page, two writes that let an operator's wording outrank the machine's,
// one status read, and the Terminology panel's CRUD. Every call goes through the shared request
// helper — never raw fetch in components.
import { request } from './client.js';

import type {
  ContentEntityType, GlossaryTerm, ResolveAnswer, ResolveEntry,
} from '../../shared/contentTranslation.js';
import type { TranslationLang } from '../../shared/language.js';

export interface ResolveItem {
  entity_type: ContentEntityType;
  entity_id: number;
  fields: string[];
}

export interface TranslationStatusPayload {
  engagement_id: number;
  entities: number;
  fields: number;
  undetected_fields: number;
  expected: number;
  translated: number;
  pending: number;
  stale_machine: number;
  stale_operator: number;
  operator: number;
  flagged: number;
  by_lang: Record<TranslationLang, { expected: number; translated: number; pending: number; stale: number }>;
  glossary: { active: number; proposed: number; retired: number; hash: string };
  recheck_flagged?: string[];
}

export interface GlossaryPayload {
  engagement_id: number;
  terms: GlossaryTerm[];
  counts: { proposed: number; active: number; retired: number };
  hash: string;
}

export interface TermInput {
  term_de: string;
  term_en?: string;
  term_fr?: string;
  invariant?: boolean;
  note?: string | null;
  status?: 'proposed' | 'active' | 'retired';
}

export const translationsApi = {
  /** ONE request for a whole page. `include_original` is for surfaces that do not already hold
   *  the record — `<Tx>` does, so it leaves this off. */
  resolve: (body: { engagement_id: number; lang: TranslationLang; items: ResolveItem[]; include_original?: boolean }) =>
    request<ResolveAnswer>('/translations/resolve', { method: 'POST', body: JSON.stringify(body) }),

  /** The operator's own wording. Outranks the machine from here on. */
  put: (entityType: string, entityId: number, field: string, lang: TranslationLang, text: string) =>
    request<ResolveEntry>(`/translations/${entityType}/${entityId}/${encodeURIComponent(field)}/${lang}`, {
      method: 'PUT', body: JSON.stringify({ text }),
    }),

  /** Back to the machine — on an operator row this discards a human's wording, so the UI asks. */
  revert: (entityType: string, entityId: number, field: string, lang: TranslationLang) =>
    request<ResolveEntry>(`/translations/${entityType}/${entityId}/${encodeURIComponent(field)}/${lang}`, { method: 'DELETE' }),

  status: (engagementId: number, check = false) =>
    request<TranslationStatusPayload>(`/engagements/${engagementId}/translation-status${check ? '?check=1' : ''}`),

  glossary: (engagementId: number) => request<GlossaryPayload>(`/engagements/${engagementId}/glossary`),

  createTerm: (engagementId: number, body: TermInput) =>
    request<GlossaryTerm>(`/engagements/${engagementId}/glossary`, { method: 'POST', body: JSON.stringify(body) }),

  patchTerm: (engagementId: number, termId: number, body: Partial<TermInput>) =>
    request<GlossaryTerm>(`/engagements/${engagementId}/glossary/${termId}`, { method: 'PATCH', body: JSON.stringify(body) }),

  /** Only a PROPOSED term can be discarded; one that has been in use is retired, never deleted. */
  discardTerm: (engagementId: number, termId: number) =>
    request<void>(`/engagements/${engagementId}/glossary/${termId}`, { method: 'DELETE' }),

  seed: (engagementId: number) =>
    request<{ engagement_id: number; candidates: number; proposed: GlossaryTerm[]; note?: string }>(
      `/engagements/${engagementId}/glossary/seed`, { method: 'POST', body: '{}' },
    ),
};
